import { subject } from "@casl/ability";
import { useAbility } from "@casl/react";
import { type FC, useState } from "react";

import type { AppAbility } from "@recobra/core";
import { m } from "@recobra/i18n/messages";
import { OPEN_TASK_STATUSES, TASK_CANCELLATION_REASON_MAX, type Task } from "@recobra/schemas/task";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@recobra/ui/alert-dialog";
import { Button } from "@recobra/ui/button";
import { Field, FieldLabel } from "@recobra/ui/field";
import { Textarea } from "@recobra/ui/textarea";

import { useAuth } from "../../lib";
import { useUpdateTaskMutation } from "../task-data";

/** Cancel activity for open tasks, or Reopen activity for canceled ones, when the user may. */
export const TaskLifecycleAction: FC<{ task: Task }> = ({ task }) => {
  const ability = useAbility<AppAbility>();
  const { user } = useAuth();
  const [isCancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const mutation = useUpdateTaskMutation({
    username: user.username,
    contextType: task.contextType,
    contextId: task.contextId,
    onSuccess: () => {
      setCancelDialogOpen(false);
      setReason("");
    },
  });
  const taskSubject = subject("Task", task);

  if (task.status === "canceled") {
    if (ability.cannot("reopen", taskSubject)) return null;
    return (
      <Button
        variant="outline"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate({ id: task.id, input: { transition: "reopen" } })}
      >
        {m.task_reopen_activity()}
      </Button>
    );
  }
  if (!OPEN_TASK_STATUSES.includes(task.status) || ability.cannot("cancel", taskSubject)) {
    return null;
  }

  const formId = `cancel-task-${task.id}`;
  const canSubmit = reason.trim() !== "" && !mutation.isPending;
  return (
    <>
      <Button variant="outline" onClick={() => setCancelDialogOpen(true)}>
        {m.task_cancel_activity()}
      </Button>
      <AlertDialog
        open={isCancelDialogOpen}
        onOpenChange={(open) => {
          if (!mutation.isPending) setCancelDialogOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.task_cancel_activity()}</AlertDialogTitle>
            <AlertDialogDescription>{m.task_cancel_activity_description()}</AlertDialogDescription>
          </AlertDialogHeader>
          <form
            id={formId}
            onSubmit={(event) => {
              event.preventDefault();
              if (!canSubmit) return;
              mutation.mutate({
                id: task.id,
                input: { transition: "cancel", cancellationReason: reason },
              });
            }}
          >
            <Field>
              <FieldLabel htmlFor={`${formId}-reason`}>{m.task_cancellation_reason()}</FieldLabel>
              <Textarea
                id={`${formId}-reason`}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                required
                maxLength={TASK_CANCELLATION_REASON_MAX}
                disabled={mutation.isPending}
              />
            </Field>
          </form>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>{m.dialog_close()}</AlertDialogCancel>
            <Button type="submit" form={formId} variant="destructive" disabled={!canSubmit}>
              {m.task_cancel_activity()}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
